import React, {
  useEffect,
  useState
} from 'react';

import {
  useParams
} from 'react-router-dom';

import './PurchaseOrderResponse.css';

const money = value =>
  '\u20A6' +
  Number(value || 0).toLocaleString(
    'en-NG',
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }
  );

const displayDate = value => {
  if (!value) return 'Not specified';

  const date = new Date(
    String(value).length === 10
      ? value + 'T00:00:00'
      : value
  );

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString(
    'en-NG',
    {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }
  );
};

async function responseJson(response) {
  const payload =
    await response.json();

  if (!response.ok) {
    const error =
      new Error(
        payload.error ||
        'The request could not be completed'
      );

    error.existingResponse =
      payload.existing_response;

    throw error;
  }

  return payload;
}

export default function PurchaseOrderResponse() {
  const { token } =
    useParams();

  const [link, setLink] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  const [error, setError] =
    useState('');

  const [completed, setCompleted] =
    useState(null);

  const [form, setForm] =
    useState({
      response: '',
      responder_name: '',
      responder_email: '',
      responder_phone: '',
      response_comment: ''
    });

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const response =
          await fetch(
            '/api/public/purchase-orders/' +
              encodeURIComponent(token),
            {
              credentials: 'omit',
              cache: 'no-store',
              headers: {
                Accept: 'application/json'
              }
            }
          );

        const payload =
          await responseJson(response);

        if (!active) return;

        setLink(payload.data);

        setForm(current => ({
          ...current,
          responder_name:
            payload.data.recipient_name ||
            ''
        }));
      } catch (requestError) {
        if (active) {
          setError(
            requestError.message ||
            'The Purchase Order could not be loaded'
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [token]);

  const updateField =
    event => {
      const {
        name,
        value
      } = event.target;

      setForm(current => ({
        ...current,
        [name]: value
      }));
    };

  const chooseResponse =
    response => {
      setForm(current => ({
        ...current,
        response
      }));

      setError('');
    };

  const submit =
    async event => {
      event.preventDefault();

      if (!form.response) {
        setError(
          'Select Accept, Request Changes or Decline.'
        );
        return;
      }

      if (!form.responder_name.trim()) {
        setError(
          'Enter the name of the person responding.'
        );
        return;
      }

      if (
        form.response !== 'accepted' &&
        !form.response_comment.trim()
      ) {
        setError(
          form.response ===
            'changes_requested'
            ? 'Explain the changes required.'
            : 'Provide the reason for declining.'
        );
        return;
      }

      if (
        !window.confirm(
          'Submit this as your final response? It cannot be edited afterward.'
        )
      ) {
        return;
      }

      setSubmitting(true);
      setError('');

      try {
        const response =
          await fetch(
            '/api/public/purchase-orders/' +
              encodeURIComponent(token) +
              '/respond',
            {
              method: 'POST',
              credentials: 'omit',
              cache: 'no-store',
              headers: {
                Accept: 'application/json',
                'Content-Type':
                  'application/json'
              },
              body:
                JSON.stringify(form)
            }
          );

        const payload =
          await responseJson(response);

        setCompleted(payload.data);

        setLink(current => ({
          ...current,
          status: 'responded'
        }));

        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } catch (requestError) {
        if (
          requestError.existingResponse
        ) {
          setCompleted(
            requestError.existingResponse
          );
        }

        setError(
          requestError.message ||
          'The response could not be recorded'
        );
      } finally {
        setSubmitting(false);
      }
    };

  if (loading) {
    return (
      <main className="po-public-page">
        <div className="po-public-card po-public-message">
          Loading secure Purchase Order...
        </div>
      </main>
    );
  }

  if (!link) {
    return (
      <main className="po-public-page">
        <div className="po-public-card">
          <header className="po-public-header">
            <h1>Bloans Creations Ltd.</h1>
            <p>Secure Purchase Order Review</p>
          </header>

          <div className="po-public-error">
            {error}
          </div>
        </div>
      </main>
    );
  }

  const document =
    link.document || {};

  const po =
    document.purchase_order || {};

  const supplier =
    document.supplier || {};

  const company =
    document.company || {};

  const authorization =
    document.authorization || {};

  const lines =
    Array.isArray(po.lines)
      ? po.lines
      : [];

  const responded =
    link.status === 'responded' ||
    Boolean(completed);

  return (
    <main className="po-public-page">
      <article className="po-public-card">
        <header className="po-public-header">
          <div>
            <h1>
              {company.name ||
                'Bloans Creations Ltd.'}
            </h1>

            <p>
              Secure Purchase Order Review
            </p>
          </div>

          <div className="po-public-reference">
            <span>Purchase Order</span>
            <strong>
              {po.number ||
                link.po_number}
            </strong>
            <small>
              Version {link.version_number}
            </small>
          </div>
        </header>

        <div className="po-public-security">
          This secure page is the official Purchase
          Order. No document or signature image was
          attached to the message you received.
        </div>

        {responded && (
          <div className="po-public-success">
            <strong>
              Response recorded
            </strong>

            <p>
              {completed
                ? completed.response ===
                    'accepted'
                  ? 'This Purchase Order was accepted.'
                  : completed.response ===
                      'changes_requested'
                    ? 'Changes were requested.'
                    : 'This Purchase Order was declined.'
                : 'A final response has already been recorded for this link.'}
            </p>
          </div>
        )}

        <section className="po-public-grid">
          <div className="po-public-panel">
            <h2>Supplier</h2>

            <strong>
              {supplier.name ||
                link.recipient_name}
            </strong>

            {supplier.address && (
              <p>{supplier.address}</p>
            )}

            {supplier.phone && (
              <p>{supplier.phone}</p>
            )}

            {supplier.email && (
              <p>{supplier.email}</p>
            )}
          </div>

          <div className="po-public-panel">
            <h2>Details</h2>

            <dl>
              <div>
                <dt>PO date</dt>
                <dd>
                  {displayDate(po.date)}
                </dd>
              </div>

              <div>
                <dt>Expected date</dt>
                <dd>
                  {displayDate(
                    po.expected_date
                  )}
                </dd>
              </div>

              <div>
                <dt>Link expires</dt>
                <dd>
                  {displayDate(
                    link.expires_at
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="po-public-section">
          <h2>Items Ordered</h2>

          <div className="po-public-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="number">
                    Quantity
                  </th>
                  <th className="number">
                    Unit Cost
                  </th>
                  <th className="number">
                    Total
                  </th>
                </tr>
              </thead>

              <tbody>
                {lines.map(
                  (line, index) => (
                    <tr key={index}>
                      <td>
                        <strong>
                          {line.description}
                        </strong>

                        {line.item_type && (
                          <small>
                            {line.item_type}
                          </small>
                        )}
                      </td>

                      <td className="number">
                        {Number(
                          line.quantity || 0
                        ).toLocaleString(
                          'en-NG'
                        )}

                        {line.unit_of_measure
                          ? ' ' +
                            line.unit_of_measure
                          : ''}
                      </td>

                      <td className="number">
                        {money(
                          line.unit_cost
                        )}
                      </td>

                      <td className="number strong">
                        {money(
                          line.line_total
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          <div className="po-public-total">
            <span>Purchase Order Total</span>
            <strong>{money(po.total)}</strong>
          </div>
        </section>

        <section className="po-public-grid po-public-terms">
          {company.delivery_address && (
            <div className="po-public-panel">
              <h2>Deliver To</h2>
              <p>
                {company.delivery_address}
              </p>
            </div>
          )}

          {company.payment_terms && (
            <div className="po-public-panel">
              <h2>Payment Terms</h2>
              <p>
                {company.payment_terms}
              </p>
            </div>
          )}

          {company.purchasing_terms && (
            <div className="po-public-panel">
              <h2>Purchasing Terms</h2>
              <p>
                {company.purchasing_terms}
              </p>
            </div>
          )}
        </section>

        <section className="po-public-authorisation">
          <div className="po-public-verified">
            VERIFIED
          </div>

          <div>
            <h2>
              Electronically authorised
            </h2>

            <strong>
              {authorization.signer_name ||
                authorization.approved_by}
            </strong>

            <p>
              {authorization.signer_title ||
                'Managing Director'}
            </p>

            <p>
              {displayDate(
                authorization.signed_at
              )}
            </p>
          </div>
        </section>

        {!responded && (
          <form
            className="po-public-response"
            onSubmit={submit}
          >
            <h2>Supplier Response</h2>

            <p>
              Review every item before responding.
              Your final response cannot be edited.
            </p>

            <div className="po-public-actions">
              <button
                type="button"
                className={
                  form.response ===
                    'accepted'
                    ? 'selected accept'
                    : ''
                }
                onClick={() =>
                  chooseResponse('accepted')
                }
              >
                Accept PO
              </button>

              <button
                type="button"
                className={
                  form.response ===
                    'changes_requested'
                    ? 'selected change'
                    : ''
                }
                onClick={() =>
                  chooseResponse(
                    'changes_requested'
                  )
                }
              >
                Request Changes
              </button>

              <button
                type="button"
                className={
                  form.response ===
                    'declined'
                    ? 'selected decline'
                    : ''
                }
                onClick={() =>
                  chooseResponse('declined')
                }
              >
                Decline PO
              </button>
            </div>

            <div className="po-public-form-grid">
              <label>
                Supplier representative
                <input
                  name="responder_name"
                  value={
                    form.responder_name
                  }
                  onChange={updateField}
                  maxLength={255}
                  required
                />
              </label>

              <label>
                Phone number
                <input
                  name="responder_phone"
                  value={
                    form.responder_phone
                  }
                  onChange={updateField}
                  maxLength={100}
                />
              </label>

              <label>
                Email address
                <input
                  type="email"
                  name="responder_email"
                  value={
                    form.responder_email
                  }
                  onChange={updateField}
                  maxLength={320}
                />
              </label>
            </div>

            {form.response &&
              form.response !==
                'accepted' && (
                <label>
                  {form.response ===
                    'changes_requested'
                    ? 'Changes required'
                    : 'Reason for declining'}

                  <textarea
                    name="response_comment"
                    value={
                      form.response_comment
                    }
                    onChange={updateField}
                    maxLength={4000}
                    required
                  />
                </label>
              )}

            {error && (
              <div className="po-public-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="po-public-submit"
              disabled={
                submitting ||
                !form.response
              }
            >
              {submitting
                ? 'Recording Response...'
                : 'Submit Final Response'}
            </button>
          </form>
        )}

        <footer className="po-public-footer">
          <strong>
            {company.name ||
              'Bloans Creations Ltd.'}
          </strong>

          {company.address && (
            <span>{company.address}</span>
          )}

          {company.phone && (
            <span>{company.phone}</span>
          )}

          {company.email && (
            <span>{company.email}</span>
          )}
        </footer>
      </article>
    </main>
  );
}
